import { useState } from 'react';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { useComments, type Comment } from '@/hooks/useComments';
import { CommentInput } from '@/components/comments/CommentInput';
import { CommentItem } from '@/components/comments/CommentItem';
import { ScrollArea } from '@/components/ui/scroll-area';
import { format, isToday, isYesterday, isThisYear } from 'date-fns';

import { useMentionUsers } from '@/hooks/useMentionUsers';

interface CommentsSidebarDetailProps {
    collection: string;
    itemId: string;
    defaultOpen?: boolean;
}

export function CommentsSidebarDetail({
    collection,
    itemId,
}: CommentsSidebarDetailProps) {
    const {
        comments,
        isLoading,
        createComment,
        updateComment,
        deleteComment,
        currentUser
    } = useComments(collection, itemId);

    const { data: userMap } = useMentionUsers(comments);

    const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set(['today']));

    const handleSubmit = async (content: string) => {
        await createComment.mutateAsync(content);
    };

    const handleUpdate = async (id: string, content: string) => {
        await updateComment.mutateAsync({ id, comment: content });
    };

    const handleDelete = async (id: string) => {
        await deleteComment.mutateAsync(id);
    };

    // Group comments by date
    const commentsByDate = comments.reduce((groups, comment) => {
        const date = new Date(comment.date_created);
        let dateKey: string;
        let dateLabel: string;

        if (isToday(date)) {
            dateKey = 'today';
            dateLabel = 'Today';
        } else if (isYesterday(date)) {
            dateKey = 'yesterday';
            dateLabel = 'Yesterday';
        } else if (isThisYear(date)) {
            dateKey = format(date, 'MMM d');
            dateLabel = format(date, 'MMM d');
        } else {
            dateKey = format(date, 'MMM d, yyyy');
            dateLabel = format(date, 'MMM d, yyyy');
        }

        if (!groups[dateKey]) {
            groups[dateKey] = { label: dateLabel, comments: [] };
        }
        groups[dateKey]?.comments.push(comment);
        return groups;
    }, {} as Record<string, { label: string; comments: Comment[] }>);

    const toggleDate = (dateKey: string) => {
        setExpandedDates(prev => {
            const next = new Set(prev);
            if (next.has(dateKey)) {
                next.delete(dateKey);
            } else {
                next.add(dateKey);
            }
            return next;
        });
    };

    return (
        <div className="space-y-4 p-1">
            <CommentInput
                onSubmit={handleSubmit}
                isSubmitting={createComment.isPending}
            />

            <div className="space-y-4">
                {isLoading ? (
                    <div className="flex justify-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : comments.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                        No comments yet
                    </div>
                ) : (
                    <ScrollArea className="h-[400px]">
                        <div className="space-y-2">
                            {Object.entries(commentsByDate).map(([dateKey, { label, comments: dateComments }]) => (
                                <div key={dateKey} className="space-y-1">
                                    {/* Date Header */}
                                    <button
                                        onClick={() => toggleDate(dateKey)}
                                        className="flex items-center gap-2 w-full px-2 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        {expandedDates.has(dateKey) ? (
                                            <ChevronDown className="h-4 w-4" />
                                        ) : (
                                            <ChevronRight className="h-4 w-4" />
                                        )}
                                        <span>{label}</span>
                                    </button>

                                    {/* Comments in this date group */}
                                    {expandedDates.has(dateKey) && (
                                        <div className="ml-2 space-y-2">
                                            {dateComments.map((comment) => (
                                                <CommentItem
                                                    key={comment.id}
                                                    comment={comment}
                                                    currentUserId={currentUser?.id}
                                                    onUpdate={handleUpdate}
                                                    onDelete={handleDelete}
                                                    userMap={userMap}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </ScrollArea>
                )}
            </div>
        </div>
    );
}
